import type {
  BulkDeleteResult,
  BulkRenameItem,
  BulkRenameResult,
} from 'switch-shared/modules/crud/base/base.dto';
import {
  CreateDto,
  UpdateDto,
} from 'switch-shared/modules/crud/base/base.service';
import {
  GatewaySignalAggregateInput,
  GatewaySignalAggregateOutput,
  GatewayStatusInput,
  GatewayStatusOutput,
  SearchGatewayHeartbeatsOutput,
  SearchGatewayHeartbeatsQuery,
} from 'switch-shared/modules/crud/gateway-heartbeat/gateway-heartbeat.dto';
import {
  SearchGatewayResponseLogResult,
  SearchGatewayResponseLogsQuery,
} from 'switch-shared/modules/crud/gateway-response-log/gateway-response-log.dto';
import {
  GatewayCountOnlineOutput,
  GatewayCountOnlineQuery,
  LinkGatewayDto,
  SearchGateway,
  SearchGatewaysOutput,
  SearchGatewaysQuery,
} from 'switch-shared/modules/crud/gateway/gateway.dto';
import { Gateway } from 'switch-shared/modules/crud/gateway/gateway.entity';
import {
  ProxyAllowlistEntry,
  ProxyDetails,
} from 'switch-shared/modules/crud/meter-task/integration-server.service';
import {
  CreateMeterTasksDto,
  CreateMeterTasksOutput,
  SearchMeterTasksOutput,
  SearchMeterTasksQuery,
} from 'switch-shared/modules/crud/meter-task/meter-task.dto';
import { SearchSmsLogsQuery } from 'switch-shared/modules/crud/sms-log/sms-log.dto';
import { SimControlSim } from 'switch-shared/modules/external/simcontrol/simcontrol.dto';
import {
  ConnectivityCheckEvent,
  FirmwareStatus,
  GatewayStatus,
  SearchGatewayMessagesOutput,
  SimSettings,
  TcpServerSettings,
  WifiNetwork,
  WifiSettings,
} from 'switch-shared/modules/features/gateway-onboarding/gateway-onboarding.dto';
import type {
  Rut956Provisioning,
  Rut956ProvisioningBundle,
  ProvisionRut956Input,
} from 'switch-shared/modules/features/gateway-onboarding/gateway-onboarding.dto';
import type { SerialSettings } from 'switch-shared/types/serial-comms';
import { Serialized } from 'switch-shared/types/serialised';

import { baseAPI, setToast } from './baseAPI.service';
import { API_METHODS, ENDPOINT_URLS, TagId } from './config/apiTags';
import { createStreamingQuery, StreamingResponse } from './streaming.helper';

/**
 * `runId` is a client-side nonce: each distinct value gets its own RTK cache
 * entry, forcing a fresh connectivity-check stream per click.
 */
export interface ConnectivityCheckArg {
  imei: string;
  meterIds?: string[];
  runId: number;
}

export const gatewayAPI = baseAPI
  .enhanceEndpoints({
    addTagTypes: [
      ENDPOINT_URLS.GATEWAY,
      ENDPOINT_URLS.METER_TASK,
      'Proxy',
      'ProxyAllowlist',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      searchGateways: builder.query<SearchGatewaysOutput, SearchGatewaysQuery>({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.GATEWAY}/search`,
            method: API_METHODS.GET,
            params,
          };
        },
        providesTags: (res) => [
          ...(res?.results ?? []).map((con) => ({
            type: ENDPOINT_URLS.GATEWAY,
            id: con._id,
          })),
          { type: ENDPOINT_URLS.GATEWAY, id: TagId.PARTIAL_LIST },
        ],
      }),

      getGatewayById: builder.query<SearchGateway, string>({
        query: (id) => `${ENDPOINT_URLS.GATEWAY}/${id}`,
        providesTags: (_res, _err, id) => [{ type: ENDPOINT_URLS.GATEWAY, id }],
      }),

      createGateway: builder.mutation<Gateway, CreateDto<Gateway>>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.GATEWAY}`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [
          { type: ENDPOINT_URLS.GATEWAY, id: TagId.PARTIAL_LIST },
        ],
      }),

      updateGateway: builder.mutation<
        Gateway,
        { id: string; data: UpdateDto<Gateway> }
      >({
        query: ({ id, data }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${id}`,
          method: API_METHODS.PATCH,
          body: data,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          { type: ENDPOINT_URLS.GATEWAY, id },
          { type: ENDPOINT_URLS.GATEWAY, id: TagId.PARTIAL_LIST },
        ],
      }),

      bulkRenameGateways: builder.mutation<
        BulkRenameResult[],
        BulkRenameItem[]
      >({
        query: (items) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/bulk-rename`,
          method: API_METHODS.PATCH,
          body: { items },
        }),
        invalidatesTags: (_res, _err, items) => [
          ...items.map(({ id }) => ({ type: ENDPOINT_URLS.GATEWAY, id })),
          { type: ENDPOINT_URLS.GATEWAY, id: TagId.PARTIAL_LIST },
        ],
      }),

      getAllResponseLogsByIMEI: builder.query<
        SearchGatewayResponseLogResult,
        SearchGatewayResponseLogsQuery & { imei: string }
      >({
        query: ({ imei, ...params }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/response-logs`,
          params,
        }),
        providesTags: (res, _err, _) =>
          res?.data.map((con) => ({
            type: ENDPOINT_URLS.GATEWAY,
            id: con._id,
          })) ?? [],
      }),

      readGateway: builder.mutation<void, string>({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/read-gateway`,
          method: API_METHODS.POST,
        }),
      }),

      getOnlineGateways: builder.query<
        GatewayCountOnlineOutput,
        GatewayCountOnlineQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.GATEWAY}/countOnline`,
            params,
          };
        },
      }),

      bulkDeleteGateway: builder.mutation<BulkDeleteResult[], string[]>({
        query: (ids) => ({
          url: `${ENDPOINT_URLS.GATEWAY}`,
          method: API_METHODS.DELETE,
          body: ids,
        }),
        invalidatesTags: [ENDPOINT_URLS.GATEWAY],
      }),

      bulkTransferGateway: builder.mutation<
        Gateway[],
        { ids: string[]; applicationId: string }
      >({
        query: ({ ids, applicationId }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/bulk-transfer`,
          method: API_METHODS.PATCH,
          body: { ids, applicationId },
        }),
        invalidatesTags: [ENDPOINT_URLS.GATEWAY],
      }),

      deleteModbusCommand: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `${ENDPOINT_URLS.MODBUS_COMMAND}/${id}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, id) => [
          {
            type: ENDPOINT_URLS.MODBUS_COMMAND,
            id,
          },
        ],
      }),

      getGatewayHeartbeats: builder.query<
        SearchGatewayHeartbeatsOutput,
        SearchGatewayHeartbeatsQuery
      >({
        query: (params) => {
          return {
            url: `${ENDPOINT_URLS.GATEWAY}/heartbeats`,
            params,
          };
        },
      }),

      getGatewayTasks: builder.query<
        Serialized<SearchMeterTasksOutput>,
        { id: string } & SearchMeterTasksQuery
      >({
        query: ({ id, ...params }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${id}/tasks`,
          params,
        }),
        providesTags: (res) =>
          res?.data.map((task) => ({
            type: ENDPOINT_URLS.METER_TASK,
            id: task._id,
          })) ?? [],
      }),

      createGatewayTasks: builder.mutation<
        CreateMeterTasksOutput,
        { id: string } & CreateMeterTasksDto
      >({
        query: ({ id, ...body }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${id}/tasks`,
          method: API_METHODS.POST,
          body,
        }),
        invalidatesTags: [ENDPOINT_URLS.METER_TASK],
      }),

      configureGatewayViaSms: builder.mutation<Gateway, { imei: string }>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/configure-gateway-via-sms`,
          method: API_METHODS.POST,
          body: data,
        }),
      }),

      getGatewayStatus: builder.query<GatewayStatus, { imei: string }>({
        query: ({ imei }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/status`,
          method: API_METHODS.GET,
        }),
      }),

      getRut956Provisioning: builder.query<Rut956Provisioning, string>({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY_ONBOARDING}/rut956/${imei}`,
          method: API_METHODS.GET,
        }),
      }),

      provisionRut956: builder.mutation<
        Rut956Provisioning,
        { imei: string } & ProvisionRut956Input
      >({
        query: ({ imei, ...body }) => ({
          url: `${ENDPOINT_URLS.GATEWAY_ONBOARDING}/rut956/${imei}/provision`,
          method: API_METHODS.POST,
          body,
        }),
        async onQueryStarted({ imei }, { dispatch, queryFulfilled }) {
          const { data } = await queryFulfilled;
          dispatch(
            gatewayAPI.util.upsertQueryData('getRut956Provisioning', imei, data)
          );
        },
      }),

      /** Holds private keys, so the result is dropped as soon as it is unused. */
      getRut956ProvisioningBundle: builder.query<
        Rut956ProvisioningBundle,
        string
      >({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY_ONBOARDING}/rut956/${imei}/bundle`,
          method: API_METHODS.GET,
        }),
        keepUnusedDataFor: 0,
      }),

      getGatewayStatusHistory: builder.query<
        GatewayStatusOutput,
        GatewayStatusInput
      >({
        query: ({ imei, ...params }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/statusHistory`,
          method: API_METHODS.GET,
          params: params,
        }),
      }),

      getGatewaySignalAggregate: builder.query<
        GatewaySignalAggregateOutput,
        GatewaySignalAggregateInput
      >({
        query: ({ imei, ...params }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/signalAggregate`,
          method: API_METHODS.GET,
          params: params,
        }),
      }),

      getGatewayFirmwareVersions: builder.query<FirmwareStatus, string>({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/firmware-versions`,
          method: API_METHODS.GET,
        }),
      }),

      checkGatewayConnectivity: builder.query<
        StreamingResponse<Serialized<ConnectivityCheckEvent>>,
        ConnectivityCheckArg
      >(
        createStreamingQuery<
          ConnectivityCheckArg,
          Serialized<ConnectivityCheckEvent>
        >({
          url: ({ imei }) =>
            `${ENDPOINT_URLS.GATEWAY}/${imei}/connectivity-check`,
          method: 'POST',
          body: ({ meterIds }) => ({ meterIds }),
        })
      ),

      getGatewayHistory: builder.query<
        SearchGatewayMessagesOutput,
        SearchSmsLogsQuery & { imei: string }
      >({
        query: ({ imei, ...params }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/history`,
          method: API_METHODS.GET,
          params,
        }),
        providesTags: (res) =>
          res?.data.map((msg) => ({
            type: ENDPOINT_URLS.GATEWAY,
            id: msg.id,
          })) ?? [],
      }),

      requestGatewayStatus: builder.mutation<
        void,
        {
          imei: string;

          /** Suppresses all toasts for this invocation (see rtkQueryMiddleware). */
          silent?: boolean;
        }
      >({
        query: ({ imei }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/request-status`,
          method: API_METHODS.POST,
        }),
      }),

      requestGatewayRestart: builder.mutation<void, string>({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/request-restart`,
          method: API_METHODS.POST,
        }),
      }),

      configureGatewaySerial: builder.mutation<
        void,
        { imei: string; settings: SerialSettings }
      >({
        query: ({ imei, settings }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/configure-serial`,
          method: API_METHODS.POST,
          body: settings,
        }),
      }),

      configureGatewayTcp: builder.mutation<
        void,
        { imei: string; settings: TcpServerSettings }
      >({
        query: ({ imei, settings }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/configure-tcp`,
          method: API_METHODS.POST,
          body: settings,
        }),
      }),

      configureGatewaySim: builder.mutation<
        void,
        { imei: string; settings: SimSettings }
      >({
        query: ({ imei, settings }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/configure-sim`,
          method: API_METHODS.POST,
          body: settings,
        }),
      }),

      configureGatewayWifi: builder.mutation<
        void,
        { imei: string; settings: WifiSettings }
      >({
        query: ({ imei, settings }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/configure-wifi`,
          method: API_METHODS.POST,
          body: settings,
        }),
      }),

      scanGatewayWifi: builder.mutation<WifiNetwork[], string>({
        query: (imei) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/scan-wifi`,
          method: API_METHODS.POST,
        }),
      }),

      requestGatewayFirmwareChange: builder.mutation<
        void,
        { imei: string; version: string }
      >({
        query: ({ imei, version }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${imei}/firmware-change`,
          method: API_METHODS.POST,
          body: { version },
        }),
      }),

      linkByIMEIOrICCID: builder.mutation<Gateway, LinkGatewayDto>({
        query: (data) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/link`,
          method: API_METHODS.POST,
          body: data,
        }),
        invalidatesTags: [ENDPOINT_URLS.GATEWAY],
      }),

      requestSimBalance: builder.mutation<
        void,
        { msisdn: string; gatewayId: string }
      >({
        query: ({ msisdn }) => ({
          url: 'simcontrol/balance-check',
          method: API_METHODS.POST,
          body: { msisdn },
        }),
        onQueryStarted: async ({ gatewayId }, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          setTimeout(() => {
            dispatch(
              baseAPI.util.invalidateTags([
                { type: ENDPOINT_URLS.GATEWAY, id: gatewayId },
              ])
            );
          }, 5000);
        },
      }),

      lookupSim: builder.query<
        SimControlSim | null,
        { iccid?: string; msisdn?: string; applicationId?: string }
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/sim-lookup`,
          method: API_METHODS.GET,
          params,
        }),
        transformResponse: (res: { sim: SimControlSim | null }) => res.sim,
      }),

      searchSims: builder.query<
        SimControlSim[],
        { iccid?: string; msisdn?: string; applicationId?: string }
      >({
        query: (params) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/sim-search`,
          method: API_METHODS.GET,
          params,
        }),
        transformResponse: (res: { sims: SimControlSim[] }) => res.sims,
      }),

      linkGatewaySim: builder.mutation<
        Gateway,
        { id: string; iccid?: string; msisdn?: string }
      >({
        query: ({ id, ...body }) => ({
          url: `${ENDPOINT_URLS.GATEWAY}/${id}/link-sim`,
          method: API_METHODS.POST,
          body,
        }),
        invalidatesTags: (_res, _err, { id }) => [
          { type: ENDPOINT_URLS.GATEWAY, id },
        ],
      }),

      syncSimStatus: builder.mutation<void, string>({
        query: (gatewayId) => ({
          url: `gateway/${gatewayId}/sync-sim`,
          method: API_METHODS.POST,
        }),
        onQueryStarted: async (gatewayId, { dispatch, queryFulfilled }) => {
          await queryFulfilled;
          setTimeout(() => {
            dispatch(
              baseAPI.util.invalidateTags([
                { type: ENDPOINT_URLS.GATEWAY, id: gatewayId },
              ])
            );
          }, 5000);
        },
      }),

      ///////////////////////
      // Proxy & Allowlist //
      ///////////////////////

      getProxy: builder.query<ProxyDetails | null, string>({
        query: (imei) => ({
          url: `gateway/${imei}/proxy`,
          method: API_METHODS.GET,
        }),
        providesTags: (_res, _err, imei) => [{ type: 'Proxy', id: imei }],
      }),

      startProxy: builder.mutation<ProxyDetails, string>({
        query: (imei) => ({
          url: `gateway/${imei}/proxy`,
          method: API_METHODS.POST,
        }),
        invalidatesTags: (_res, _err, imei) => [{ type: 'Proxy', id: imei }],
      }),

      stopProxy: builder.mutation<void, string>({
        query: (imei) => ({
          url: `gateway/${imei}/proxy`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: (_res, _err, imei) => [{ type: 'Proxy', id: imei }],
      }),

      /**
       * Resolves the browser's public IP address, which is the address the
       * integration server sees when this machine connects to a proxy.
       *
       * Deliberately a raw `fetch` rather than the shared base query: that one
       * attaches the user's Firebase token to every request, and this request
       * leaves our domain. Failures resolve to `null` instead of rejecting, so
       * a third-party outage degrades to manual entry rather than an error
       * toast.
       */
      getMyPublicIp: builder.query<string | null, void>({
        queryFn: async () => {
          try {
            const res = await fetch('https://api.ipify.org?format=json');
            if (!res.ok) return { data: null };

            const body = (await res.json()) as { ip?: string };
            return { data: body.ip ?? null };
          } catch {
            return { data: null };
          }
        },
      }),

      listAllowlist: builder.query<{ entries: ProxyAllowlistEntry[] }, void>({
        query: () => ({
          url: `gateway/allowlist`,
          method: API_METHODS.GET,
        }),
        providesTags: ['ProxyAllowlist'],
      }),

      addAllowlist: builder.mutation<ProxyAllowlistEntry, string>({
        query: (ip) => ({
          url: `gateway/allowlist/${ip}`,
          method: API_METHODS.POST,
        }),
        invalidatesTags: ['ProxyAllowlist'],
      }),

      removeAllowlist: builder.mutation<void, string>({
        query: (ip) => ({
          url: `gateway/allowlist/${ip}`,
          method: API_METHODS.DELETE,
        }),
        invalidatesTags: ['ProxyAllowlist'],
      }),
    }),
  });

// The composer reports created/failed counts itself.
setToast('createGatewayTasks', { pending: false, fulfilled: false });

setToast('linkByIMEIOrICCID', { pending: 'Linking gateway...' });
setToast('createGateway', { pending: 0, fulfilled: 'Gateway created' });
setToast('updateGateway', { pending: 0, fulfilled: 'Gateway updated' });
setToast('requestGatewayRestart', {
  pending: 0,
  fulfilled: 'Restart sent — this can take a moment',
});
setToast('requestGatewayStatus', {
  pending: 0,
  fulfilled: 'Status requested — the response can take a moment',
});
setToast('provisionRut956', { fulfilled: 'RUT956 provisioned' });
setToast('scanGatewayWifi', { pending: 0, fulfilled: 'Network scan started' });
setToast('configureGatewayWifi', {
  pending: 0,
  fulfilled: 'Wi-Fi settings sent',
});
setToast('configureGatewaySim', {
  pending: 0,
  fulfilled: 'SIM settings sent',
});
setToast('configureGatewaySerial', {
  pending: 0,
  fulfilled: 'Serial settings sent',
});
setToast('configureGatewayTcp', {
  pending: 0,
  fulfilled: 'TCP server settings sent',
});
setToast('requestSimBalance', { pending: 'Requesting SIM balance...' });
setToast('linkGatewaySim', { pending: 0, fulfilled: 'SIM linked' });
setToast('requestGatewayFirmwareChange', {
  pending: 0,
  fulfilled: 'Firmware update sent — takes a few minutes',
});

setToast('startProxy', {
  pending: 0,
  fulfilled: 0,
});
setToast('stopProxy', {
  pending: 0,
  fulfilled: 0,
});

export const {
  useSearchGatewaysQuery,
  useGetAllResponseLogsByIMEIQuery,
  useGetGatewayByIdQuery,
  useCreateGatewayMutation,
  useUpdateGatewayMutation,
  useBulkRenameGatewaysMutation,
  useReadGatewayMutation,
  useBulkDeleteGatewayMutation,
  useBulkTransferGatewayMutation,
  useGetOnlineGatewaysQuery,
  useDeleteModbusCommandMutation,
  useGetGatewayHeartbeatsQuery,
  useGetGatewayTasksQuery,
  useCreateGatewayTasksMutation,
  useConfigureGatewayViaSmsMutation,
  useLinkByIMEIOrICCIDMutation,
  useRequestSimBalanceMutation,
  useSyncSimStatusMutation,
  useLookupSimQuery,
  useLazyLookupSimQuery,
  useSearchSimsQuery,
  useLinkGatewaySimMutation,
  useGetGatewayStatusQuery,
  useGetRut956ProvisioningQuery,
  useProvisionRut956Mutation,
  useLazyGetRut956ProvisioningBundleQuery,
  useGetGatewayFirmwareVersionsQuery,
  useGetGatewayHistoryQuery,
  useRequestGatewayStatusMutation,
  useRequestGatewayRestartMutation,
  useConfigureGatewaySerialMutation,
  useConfigureGatewayTcpMutation,
  useConfigureGatewaySimMutation,
  useConfigureGatewayWifiMutation,
  useScanGatewayWifiMutation,
  useRequestGatewayFirmwareChangeMutation,
  useCheckGatewayConnectivityQuery,
  useGetGatewayStatusHistoryQuery,
  useGetGatewaySignalAggregateQuery,
  useGetProxyQuery,
  useStartProxyMutation,
  useStopProxyMutation,
  useListAllowlistQuery,
  useAddAllowlistMutation,
  useRemoveAllowlistMutation,
  useGetMyPublicIpQuery,
} = gatewayAPI;

setToast('bulkDeleteGateway', {
  bulkResults: { noun: 'gateway', verb: 'delete' },
});
setToast('bulkRenameGateways', {
  bulkResults: { noun: 'gateway', verb: 'rename' },
});
